/** Mirror of VersionedContent*V1; this slice accepts MissionTemplate only. */
export const pinFields = [
  'kind',
  'id',
  'version',
  'schemaVersion',
  'canonicalizationVersion',
  'digest',
] as const;
export type ExactPin = Readonly<Record<(typeof pinFields)[number], string>>;
export const readStatuses = [
  'VERIFIED',
  'MISSING_REFERENCE',
  'SOURCE_UNAVAILABLE',
  'INTEGRITY_MISMATCH',
  'IDENTITY_MISMATCH',
  'UNSUPPORTED_SCHEMA',
  'UNSUPPORTED_CANONICALIZATION',
  'UNSUPPORTED_REPRESENTATION',
] as const;
export type ReadStatus = (typeof readStatuses)[number];
export type JsonValue = null | boolean | number | string | readonly JsonValue[] | JsonObject;
export interface JsonObject {
  readonly [key: string]: JsonValue;
}
export interface ContentSnapshot {
  readonly pin: ExactPin;
  /** Immutable byte mirror; no digest or canonicalization is computed here. */
  readonly canonicalContent: readonly number[];
}
export type MissionTemplateReadResult = Readonly<
  {
    query: ExactPin;
    authority: 'NON_AUTHORITY';
  } & (
    | { status: 'VERIFIED'; snapshot: ContentSnapshot }
    | {
        status: Exclude<ReadStatus, 'VERIFIED'>;
        snapshot: null;
      }
  )
>;
export interface MissionTemplateReadPort {
  read(query: ExactPin): Promise<MissionTemplateReadResult>;
}
export function exactPin(value: ExactPin): ExactPin {
  if (
    value.kind !== 'MissionTemplate' ||
    !/^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/.test(value.id) ||
    !/^[1-9][0-9]{0,63}$/.test(value.version) ||
    !/^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/.test(value.schemaVersion) ||
    !/^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/.test(value.canonicalizationVersion) ||
    !/^[0-9a-f]{64}$/.test(value.digest)
  )
    throw new Error('Invalid exact MissionTemplate pin');
  return Object.freeze(Object.fromEntries(pinFields.map((key) => [key, value[key]])) as ExactPin);
}
export function samePin(a: ExactPin, b: ExactPin): boolean {
  return pinFields.every((key) => a[key] === b[key]);
}

/** Mirror constructor: matching snapshot only for VERIFIED, no content on failures. */
export function missionTemplateReadResult(
  status: ReadStatus,
  input: ExactPin,
  snapshot: ContentSnapshot | null,
): MissionTemplateReadResult {
  const query = exactPin(input);
  if (!readStatuses.includes(status)) throw new Error('Invalid contract status');
  if (status !== 'VERIFIED') {
    if (snapshot !== null) throw new Error('Failed result cannot carry content');
    return Object.freeze({ status, query, snapshot: null, authority: 'NON_AUTHORITY' });
  }
  if (
    !snapshot ||
    !samePin(query, snapshot.pin) ||
    snapshot.canonicalContent.length === 0 ||
    snapshot.canonicalContent.some((byte) => !Number.isInteger(byte) || byte < 0 || byte > 255)
  )
    throw new Error('Verified result requires matching content');
  return Object.freeze({
    status,
    query,
    authority: 'NON_AUTHORITY',
    snapshot: Object.freeze({
      pin: exactPin(snapshot.pin),
      canonicalContent: Object.freeze([...snapshot.canonicalContent]),
    }),
  });
}
